import React, { FC } from 'react';
import { useDataProvider } from 'react-admin';
import { useQuery } from 'react-query';
import EducationStageSubjectsList from './EducationLevelSubjectsList';
import { SubjectsManager } from 'src/managers/Subjects/SubjectsManager';
import { CONSULTATION_TYPE, EducationLevelGroup, EducationStage, Resource, SUBJECT_TYPE } from 'src/constants';
import { SubjectGroupList } from 'src/api/REST/subjects/types';
import { SubjectData } from 'src/api/REST/subjects/interfaces';

type GroupedByConsultationType = {
    [key in CONSULTATION_TYPE]?: SubjectData[];
};

function groupByConsultationType(items: SubjectData[]) {
    if (items) {
        return items.reduce<GroupedByConsultationType>((acc, subj) => {
            if (subj.subject_type === SUBJECT_TYPE.PSYCHOLOGY && subj.consultationType) {
                if (!acc[subj.consultationType]) {
                    acc[subj?.consultationType] = [];
                }
                acc[subj.consultationType]!.push(subj);
            }
            return acc;
        }, {});
    }
}

const EducationLevelSubjects: FC<{
    educationLevelGroup: EducationLevelGroup | SUBJECT_TYPE.PSYCHOLOGY | null;
    reloadKey: boolean;
    refresh: () => void;
}> = ({ educationLevelGroup, reloadKey, refresh }) => {
    if (!educationLevelGroup) return null;
    const isPsycologyTab = educationLevelGroup === SUBJECT_TYPE.PSYCHOLOGY;
    const params = isPsycologyTab
        ? { educationLevelGroup: 'highSchool', subjectTypes: SUBJECT_TYPE.PSYCHOLOGY }
        : { educationLevelGroup, subjectTypes: SubjectsManager.getSubjectTypesBasic().join(',') };

    const dataProvider = useDataProvider();
    const { data } = useQuery<SubjectGroupList>([educationLevelGroup, reloadKey], () =>
        dataProvider.getGroupList(Resource.SUBJECTS, {
            meta: { ...params }
        })
    );
    if (!data || !Object.entries(data).length) return null;

    if (isPsycologyTab) {
        const psycologyData = groupByConsultationType(data.GRADE5);
        return (
            <>
                {Object.entries(psycologyData as GroupedByConsultationType).map(([consultationType, subjects]) => (
                    <EducationStageSubjectsList
                        header={consultationType as CONSULTATION_TYPE}
                        data={subjects}
                        key={consultationType}
                        isPsycologyTab={isPsycologyTab}
                        refresh={refresh}
                    />
                ))}
            </>
        );
    }

    return (
        <>
            {Object.entries(data).map(([educationStage, subjects]) => (
                <EducationStageSubjectsList
                    header={educationStage as EducationStage}
                    data={subjects}
                    key={educationStage}
                    refresh={refresh}
                />
            ))}
        </>
    );
};

export default EducationLevelSubjects;
